import React, { useContext, useEffect, useState } from 'react';
import { Card, Form, Tag } from 'antd';
import { toLower } from 'lodash';
import { useTranslation } from 'react-i18next';

import ButtonComponent from '@/components/Button';
import TableComponent from '@/components/Table';
import {
  GATEWAY_STATUSES,
  PAGE_SIZE,
  SEND_TYPES,
  SMS_STATUSES,
  SMS_TYPES,
} from '@/constants';
import { API } from '@/constants/api';
import InputComponent from '@/components/Input';
import SelectInput from '@/components/Select';
import { parseDateTime } from '@/utils/formatter';
import { SmsManagementService } from '@/services';
import { GlobalContext } from '@/contexts/GlobalContext';
import { ReactComponent as SearchIcon } from '@/assets/images/icons/search.svg';
import EllipsisTooltip from '@/components/EllipsisTooltip';

import './index.scss';
import moment from 'moment';
import RangePickerComponent from '@/components/RangePicker';
import SelectFetching from '@/components/SelectFetching';
import { getCurrentCountry } from '@/utils/domain';
import { shouldHideSender } from '@/utils/hideSender';

const dateFormat = 'DD-MM-YYYY';

function Reports() {
  const [filterFormInstance] = Form.useForm();
  const [filterParams, setFilterParams] = useState({});
  const [triggerRefreshTable, setTriggerRefreshTable] = useState(false);
  const { t } = useTranslation();
  const { setPageTitle, setTriggerFetchProfile } = useContext(GlobalContext);

  // get the current Country
  const country = getCurrentCountry();

  const columns = [
    ...(shouldHideSender(country)
      ? []
      : [
          {
            title: t('sms_add_new_sender.sender_name'),
            dataIndex: 'senderName',
            key: 'senderName',
            ellipsis: {
              showTitle: false,
            },
            render: (_, { senderName: _senderName }) => (
              <div className="fw-500">
                <EllipsisTooltip title={_senderName}>
                  {_senderName}
                </EllipsisTooltip>
              </div>
            ),
          },
        ]),
    {
      title: t('reports.created_date'),
      dataIndex: 'createdAt',
      key: 'createdDate',
      width: '160px',
      render: (_, { createdAt }) => <span>{parseDateTime(createdAt)}</span>,
    },
    {
      title: t('reports.receive_number'),
      dataIndex: 'toPhoneNumber',
      key: 'receiveNumber',
      render: (_, { toPhoneNumber }) => <span>{toPhoneNumber}</span>,
    },
    {
      title: t('reports.count_sms'),
      dataIndex: 'creditAmount',
      key: 'countSMS',
      width: '120px',
      render: (_, { creditAmount }) => <span>{creditAmount}</span>,
    },
    {
      title: t('reports.type_of_send'),
      dataIndex: 'sendType',
      key: 'typeOfSend',
      render: (_, { sendType }) => {
        const sendTypeText = SEND_TYPES.find(
          (item) => item.value === sendType,
        )?.label;
        return <span>{t(`common.constants.send_types.${sendTypeText}`)}</span>;
      },
    },
    {
      title: t('reports.sms_type'),
      dataIndex: 'smsType',
      key: 'smsType',
      render: (_, { smsType }) => {
        if (!smsType) {
          return null;
        }

        const smsTypeText = SMS_TYPES.find(
          (item) => item.value === smsType,
        )?.label;
        return (
          <Tag
            key={smsTypeText}
            className={`sms-tag sms-sender sms-sender-type-${toLower(
              smsTypeText,
            )}`}
          >
            <span>{t(`common.constants.sms_types.${smsTypeText}`)}</span>
          </Tag>
        );
      },
    },
    {
      title: t('reports.success_date'),
      dataIndex: 'telcoReceivedAt',
      key: 'successDate',
      width: '160px',
      render: (_, { telcoReceivedAt }) =>
        telcoReceivedAt ? <span>{parseDateTime(telcoReceivedAt)}</span> : null,
    },
    {
      title: t('reports.gateway_status'),
      dataIndex: 'channelStatus',
      key: 'getwayStatus',
      render: (_, { channelStatus }) => {
        const statusText = GATEWAY_STATUSES.find(
          (item) => item.value === channelStatus,
        )?.label;
        return (
          <Tag
            key={statusText}
            className={`sms-tag gateway-${toLower(statusText)}`}
          >
            <span>{t(`common.constants.gateway_statuses.${statusText}`)}</span>
          </Tag>
        );
      },
    },
    {
      title: t('reports.sending_status'),
      dataIndex: 'status',
      key: 'status',
      render: (_, { status }) => {
        const statusText = SMS_STATUSES.find(
          (item) => item.value === status,
        )?.label;
        return (
          <Tag
            key={statusText}
            className={`sms-tag sms-${toLower(statusText)}`}
          >
            <span>{t(`common.constants.sms_statuses.${statusText}`)}</span>
          </Tag>
        );
      },
    },
  ];
  useEffect(() => {
    setPageTitle({
      title: t('reports_page.page_title'),
    });
    setTriggerFetchProfile(true);
  }, [setPageTitle, t, setTriggerFetchProfile]);

  const handleOnFormFilterFinish = (values) => {
    setTriggerRefreshTable(true);
    const range = values.date;
    const params = {
      ...filterParams,
      ...values,
      pagination: {
        current: 1,
        pageSize: PAGE_SIZE,
      },
    };
    delete params.date;
    if (range) {
      params.fromDate = range[0].format('YYYY-MM-DD');
      params.toDate = range[1].format('YYYY-MM-DD');
    } else {
      params.fromDate = null;
      params.toDate = null;
    }
    const { senderId } = values;
    if (senderId) {
      params.senderId = senderId.value;
    }
    setFilterParams({ ...params });
    setTimeout(() => {
      setTriggerRefreshTable(false);
    }, 50);
  };

  const handleResetFilterForm = () => {
    filterFormInstance.resetFields();
    setFilterParams({});
  };

  const fetchSenderList = async (name) => {
    const res = await SmsManagementService.fetchActiveAndBlockSenderList({
      name,
    });

    if (res.success) {
      return res.data.map((sender) => ({
        label: sender.senderName,
        value: sender.id,
      }));
    }

    return [];
  };

  const [dates, setDates] = useState(null);
  const disabledDate = (current) => {
    const oneMonthAgo = moment().subtract(1, 'months');

    if (!dates) {
      return current && current < oneMonthAgo.startOf('day');
    }
    const tooLate = dates[0] && current.diff(dates[0], 'days') > 7;
    const tooEarly = dates[1] && dates[1].diff(current, 'days') > 7;

    return !!tooEarly || !!tooLate;
  };

  const onChangeDatePicker = (val) => {
    if (!val) {
      setDates(null);
    }
  };

  const smsTypeOptions = SMS_TYPES.map((status) => ({
    label: t(`common.constants.sms_types_lower.${status.label}`),
    value: status.value,
  }));

  const gatewayStatusOptions = GATEWAY_STATUSES.map((status) => ({
    label: t(`common.constants.gateway_statuses.${status.label}`),
    value: status.value,
  }));

  return (
    <div className="report-container">
      <Card title={t('reports.message_report')} bordered={false}>
        <div className="form-filter">
          <Form
            name="filter"
            layout="vertical"
            onFinish={handleOnFormFilterFinish}
            autoComplete="off"
            className="form"
            form={filterFormInstance}
            initialValues={{
              smsType: 2,
            }}
          >
            <Form.Item
              label={t('reports.receive_number')}
              name="phoneNumber"
              className="form-item"
            >
              <InputComponent placeholder={t('reports.receive_number')} />
            </Form.Item>
            <Form.Item
              label={t('transactions_page_trans.date')}
              name="date"
              className="form-item date-picker-container"
              rules={[
                {
                  required: true,
                  message: t('reports_page.form.errors.date.required'),
                },
              ]}
            >
              <RangePickerComponent
                format={dateFormat}
                name="date"
                placeholder={[
                  t('transactions_page_trans.start_date'),
                  t('transactions_page_trans.end_date'),
                ]}
                disabledDate={disabledDate}
                onCalendarChange={(val) => setDates(val)}
                inputReadOnly
                onChange={onChangeDatePicker}
              />
            </Form.Item>
            <Form.Item
              hidden={shouldHideSender(country)}
              label={t('sms_add_new_sender.sender_name')}
              name="senderId"
              className="form-item"
            >
              <SelectFetching
                allowClear
                triggerFetching
                placeholder={t('reports.all')}
                fetchOptions={fetchSenderList}
              />
            </Form.Item>
            <Form.Item
              label={t('reports.sms_type')}
              name="smsType"
              className="form-item"
            >
              <SelectInput
                placeholder={t('reports.all')}
                options={smsTypeOptions}
              />
            </Form.Item>
            <Form.Item
              label={t('reports.gateway_status')}
              name="channelStatus"
              className="form-item"
            >
              <SelectInput
                allowClear
                placeholder={t('reports.gateway_status')}
                options={gatewayStatusOptions}
              />
            </Form.Item>
            <Form.Item>
              <div className="form-actions-item">
                <ButtonComponent
                  type="primary"
                  htmlType="submit"
                  icon={<SearchIcon />}
                >
                  {t('transactions_page_trans.search')}
                </ButtonComponent>
                <ButtonComponent type="default" onClick={handleResetFilterForm}>
                  {t('transactions_page_trans.reset')}
                </ButtonComponent>
              </div>
            </Form.Item>
          </Form>
        </div>
        <TableComponent
          columns={columns}
          filterParams={filterParams}
          triggerRefresh={triggerRefreshTable}
          action={API.REPORT.GET_REPORT}
          scroll={{
            scrollToFirstRowOnChange: true,
            x: 1200,
          }}
          defaultFilter={{ smsType: 2 }}
          fetchDataOnInit={false}
        />
      </Card>
    </div>
  );
}

export default Reports;
